// apps/client/src/ui/screens/mailbox.tsx (M1-T17) — your letters, newest first (HUD Mail and the Menu): the sender, a relative time,
// an unread dot and the gift pill per row. A tap opens the letter; the primary button opens the writer.
import type { StringKey } from '@whiskerton/content';
import type { MailFrom, MailHeader } from '@whiskerton/shared';
import { useShallow } from 'zustand/react/shallow';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Button, Panel, Pill, Tag } from '../kit';
import type { ScreenDef } from '../router';
import { Ago } from './news';

declare module '../router' { interface ScreenParams { mailbox: undefined } }

/** The sender as DESIGN.md's letters show it: a resident in their friend colour, a villager on cream, the town's own senders as text. */
export function Sender({ from }: { from: MailFrom }) {
  const t = useT();
  const residents = useGame((s) => s.residents);
  if (from.kind === 'system') return <span>{t(`mail.from${from.key[0]!.toUpperCase()}${from.key.slice(1)}` as StringKey)}</span>;
  if (from.kind === 'villager') return <Tag color="villager">{from.name}</Tag>;
  if (from.id === null) return <span>{t('common.formerResident')}</span>;
  const r = residents[from.id];
  return r ? <Tag color={r.color}>{r.name}</Tag> : <span>{from.name}</span>;
}

function Row({ m, open }: { m: MailHeader; open: () => void }) {
  const t = useT();
  return (
    <li>
      <button type="button" onClick={open} style={{ width: '100%', display: 'grid', gridTemplateColumns: '12px 1fr auto auto', alignItems: 'center', gap: 10,
        padding: '10px 12px', border: 0, borderRadius: 14, background: m.read ? 'transparent' : 'var(--cream-100)', font: 'inherit', fontWeight: m.read ? 700 : 900,
        color: 'var(--wood-800)', cursor: 'pointer', textAlign: 'left' }}>
        {m.read ? <span /> : <span className="mail-unread" title={t('mail.unread')} style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--flag-500)' }} />}
        <span><Sender from={m.from} /></span>
        {m.hasItem ? <Pill>{t(m.wrapped ? 'mail.wrapped' : 'mail.gift')}</Pill> : <span />}
        <span style={{ fontSize: 14, color: 'var(--wood-600)', whiteSpace: 'nowrap' }}><Ago at={m.at} /></span>
      </button>
    </li>
  );
}

const NONE: readonly MailHeader[] = [];
function Mailbox() {
  const t = useT();
  const { mail, openScreen } = useGame(useShallow((s) => ({ mail: s.you?.mail ?? NONE, openScreen: s.openScreen })));
  const sorted = [...mail].sort((a, b) => b.at - a.at);
  return (
    <Panel title={t('mail.title')}>
      <div style={{ display: 'grid', gap: 12, width: 'min(560px, 72cqw)', maxWidth: '100%' }}>
        {sorted.length === 0 && <p style={{ margin: 0 }}>{t('mail.empty')}</p>}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
          {sorted.map((m) => <Row key={m.id} m={m} open={() => openScreen('letter', { mail: m.id })} />)}
        </ul>
        <Button onClick={() => openScreen('writer', { to: null })}>{t('mail.writeTitle')}</Button>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'mailbox'> = { id: 'mailbox', layer: 'panel', menu: { label: 'mail.title', order: 10 }, component: () => <Mailbox /> };
